import { Button, Flex } from "@qws-org/ui-kit";
import type { FC, ReactNode } from "react";

import { LogoHor } from "~/icons";

import { useMainBannerTranslations } from "./useMainBannerTranslations";

type Item = {
  text: Record<LanguageCode, string>;
  icon: ReactNode;
};

type MainBannerProps = {
  data: Array<Item>;
  playNowLink: string;
  language: LanguageCode;
  analytics: {
    logInMain: () => void;
    createAccountMain: () => void;
  };
};

export const MainBanner: FC<MainBannerProps> = ({
  data,
  playNowLink,
  language,
  analytics: { createAccountMain, logInMain },
}) => {
  const t = useMainBannerTranslations();

  const redirectToRoom = (): void => {
    if (playNowLink) {
      window.location.href = playNowLink;
    }
  };
  const onLoginMain = (): void => {
    logInMain();
    redirectToRoom();
  };
  const onCreateAccountMain = (): void => {
    createAccountMain();
    redirectToRoom();
  };

  return (
    <section
      className="text-center pt-8 pb-4 md:py-[83px] px-4"
      data-testid="main-banner"
    >
      <Flex justify="center" m={{ mb: 24 }}>
        <LogoHor
          viewBox="0 0 339 192"
          className="w-[176.56px] h-[100px] md:w-[339px] md:h-[192px]"
        />
      </Flex>

      <h1 className="bg-clip-text text-transparent bg-golden-gradient font-noto-sans-sc font-extrabold text-[28px] md:text-[48px] leading-[32px] md:leading-[64px] mb-[28px] md:mb-16">
        {t.heading.title}
      </h1>

      <div className="flex flex-wrap justify-center gap-2 md:gap-2.5 mb-14 md:mb-[76px] items-stretch">
        {data.map((feature, idx) => (
          <div
            key={idx}
            className="bg-custom-purple/40 backdrop-blur-xl pt-2.5 pb-2 md:py-5 px-2 max-w-[340px] w-[calc(100%-20px)] self-stretch transform skew-x-[-16deg]"
          >
            <div className="transform flex flex-col items-center text-center skew-x-[16deg] h-full">
              {feature.icon}
              <p className="font-noto-sans-sc text-sm mt-2 md:text-xl md:whitespace-pre-line">
                {feature.text[language]}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-2 md:gap-6">
        <Button
          variant="default"
          className=" min-w-[160px] md:text-lg md:leading-[30px] md:min-w-[240px]   "
          onPress={onCreateAccountMain}
          p={{ px: 28, py: 12 }}
          md={{ p: 17 }}
          data-testid="main-banner-create-account-button"
        >
          <p className="text-base leading-6 md:text-xl md:leading-7 text-nowrap">
            {t.buttons.create}
          </p>
        </Button>

        <Button
          variant="transparent"
          className=" min-w-[160px] md:text-lg md:leading-[30px] md:min-w-[240px]  "
          onPress={onLoginMain}
          p={{ px: 28, py: 12 }}
          md={{ p: 17 }}
          data-testid="main-banner-login-button"
        >
          <p className="text-base leading-6 md:text-xl md:leading-7 text-nowrap">
            {t.buttons.login}
          </p>
        </Button>
      </div>
    </section>
  );
};
